import { memo, useRef, useState, type ReactNode } from 'react'
import * as ContextMenu from '@radix-ui/react-context-menu'
import * as Dialog from '@radix-ui/react-dialog'
import type { VisitRow } from '@fitting-room/contracts'
import { VISIT_STATUS_TRANSITIONS } from '@fitting-room/contracts/appointment-status'
import { useSetAppointmentStatus } from '../../lib/adminQueries'
import { statusActionLabel } from '../../lib/appointments'
import { useStaffToken } from '../../lib/staffToken'

type VisitStatus = VisitRow['status']

/**
 * Change an appointment's status from where it already shows — a row of the
 * list, a chip on the month — by right-click, or a long press on a touch
 * screen. Ported from tperent's `AppointmentActionMenu`.
 *
 * The menu is drawn from `VISIT_STATUS_TRANSITIONS`, the table the API
 * refuses against, so it never offers a move that would come back a 422.
 * It goes through the same endpoint as the drawer's buttons; there is no
 * second path with different side effects.
 */

/** Forward first, the destructive one last. Every status is listed so none is silently dropped by the filter. */
const MENU_ORDER: readonly VisitStatus[] = ['completed', 'no_show', 'booked', 'cancelled']

/**
 * Only canceling asks twice. It is the one move whose mistake the customer
 * might act on — a stylist who sees "Canceled" may tell them so — and every
 * other move has an "Undo" that costs nothing.
 */
const NEEDS_CONFIRM: readonly VisitStatus[] = ['cancelled']

function AppointmentActionMenuImpl({
  visitId,
  status,
  children,
  announceMenu = false,
}: {
  readonly visitId: string
  readonly status: VisitStatus
  /** The row being wrapped; it becomes the menu's trigger. */
  readonly children: ReactNode
  /** Tell assistive tech a menu is here. Only for a trigger that can take focus. */
  readonly announceMenu?: boolean
}) {
  const { token } = useStaffToken()
  const mutation = useSetAppointmentStatus(token, visitId)
  const [confirming, setConfirming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  /*
   * A long press that opens the menu still sends a `click` when the finger
   * lifts, which would open the drawer behind the menu (and, on the month,
   * bubble to the day). The test is "did this press have to do with the
   * menu" — it was open when the press began, or it opened during it — not
   * "is it open now", which is already false by the time a click that closed
   * it arrives. tperent learned this one the hard way.
   */
  const suppressClick = useRef(false)
  const isOpen = useRef(false)

  const actions = MENU_ORDER.filter((to) => VISIT_STATUS_TRANSITIONS[status].includes(to))

  const run = (to: VisitStatus) => {
    if (NEEDS_CONFIRM.includes(to)) {
      setConfirming(true)
      return
    }
    mutation.mutate({ status: to }, { onError: (error) => setErrorMessage(error.message) })
  }

  return (
    <>
      {/*
       * Not modal. A modal menu sets `pointer-events: none` on the body and
       * puts it back when it closes — but picking "Cancel appointment" opens
       * the confirmation dialog in the same moment, which saves that `none`
       * as the body's "original" style and restores it on close. The whole
       * console then ignores every click until a reload. (The menu and the
       * dialog also come from two copies of Radix's layer code, so neither
       * knows the other is open.) A right-click menu has no need to block the
       * page behind it.
       */}
      <ContextMenu.Root
        modal={false}
        onOpenChange={(open) => {
          isOpen.current = open
          if (open) suppressClick.current = true
        }}
      >
        <ContextMenu.Trigger
          asChild
          aria-haspopup={announceMenu ? 'menu' : undefined}
          style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          onPointerDownCapture={() => {
            suppressClick.current = isOpen.current
          }}
          onClickCapture={(event) => {
            if (!suppressClick.current) return
            suppressClick.current = false
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {children}
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content className="st-menu" data-testid={`appointment-actions-${visitId}`}>
            {actions.map((to) => (
              <ContextMenu.Item key={to} className="st-menu-item" data-tone={to === 'cancelled' ? 'alert' : undefined} onSelect={() => run(to)}>
                {statusActionLabel(status, to)}
              </ContextMenu.Item>
            ))}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {/* One dialog for both the confirmation and a failure: the menu closes
          the moment something is picked, so an error has nowhere else to go. */}
      <Dialog.Root
        open={confirming || errorMessage !== null}
        onOpenChange={(open) => {
          if (open) return
          setConfirming(false)
          setErrorMessage(null)
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="st-overlay" />
          <Dialog.Content className="st-dialog" aria-describedby={undefined}>
            {errorMessage !== null ? (
              <>
                <Dialog.Title className="st-display mb-2 text-lg">That didn&rsquo;t save</Dialog.Title>
                <p className="st-meta mb-4">{errorMessage}</p>
                <div className="flex justify-end">
                  <Dialog.Close asChild>
                    <button type="button" className="st-btn-ghost">
                      OK
                    </button>
                  </Dialog.Close>
                </div>
              </>
            ) : (
              <>
                <Dialog.Title className="st-display mb-2 text-lg">Cancel this appointment?</Dialog.Title>
                <p className="st-meta mb-4">
                  Nothing is sent to the customer, and any reminder still waiting won&rsquo;t go out. You can undo it
                  from the appointment.
                </p>
                <div className="flex justify-end gap-2">
                  <Dialog.Close asChild>
                    <button type="button" className="st-btn-ghost">
                      Keep it
                    </button>
                  </Dialog.Close>
                  <button
                    type="button"
                    className="st-btn"
                    onClick={() => {
                      setConfirming(false)
                      mutation.mutate({ status: 'cancelled' }, { onError: (error) => setErrorMessage(error.message) })
                    }}
                  >
                    Cancel appointment
                  </button>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

/** One per row, a month's worth at a time; the props are primitives, so memo holds. */
export const AppointmentActionMenu = memo(AppointmentActionMenuImpl)
